import React, { useState, useEffect, useCallback, memo } from 'react';
import Sidebar from '../components/Sidebar';
import MobileHeader from '../components/MobileHeader';
import { auth, db } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, collection, onSnapshot } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

// Componente memoizado para las estadísticas
const StatCard = memo(({ title, value, bgColor, icon, textColor }) => {
  return (
    <div className={`${bgColor} rounded-lg shadow p-6 border-l-4 ${textColor}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">{title}</p>
          <p className="text-2xl font-semibold">{value}</p>
        </div>
        <div className="text-4xl">{icon}</div>
      </div>
    </div>
  );
});

function Dashboard() {  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('dashboard');  const [tickets, setTickets] = useState([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [realtimeStatus, setRealtimeStatus] = useState('Desconectado');
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  
  // Estado para configuración de descuento
  const [discountConfig, setDiscountConfig] = useState({
    discountEvery: 0,
    discountPer: 1
  });
  
  const navigate = useNavigate();
  
  // Función helper para aplicar descuento dinámico desde Firestore
  const applyTicketDiscount = (count) => {
    if (count <= 0) return count;
    // Si discountEvery es 0 o menor, no aplicar descuento
    if (discountConfig.discountEvery <= 0) return count;
    // Calcular descuento: cada discountEvery tickets, descontar discountPer
    const discount = Math.floor(count / discountConfig.discountEvery) * discountConfig.discountPer;
    return count - discount;
  };
  
  // 🚨 Función para monitorear CAs con tickets bajos
  const setupCATicketsMonitoring = useCallback(() => {
    try {
      console.log('🚨 Dashboard: Monitoreo de alertas DESACTIVADO por solicitud del cliente');
      
      // ALERTA DESACTIVADA - NO MOSTRAR MÁS ALERTAS DE TICKETS CRÍTICOS
      // const usuariosRef = collection(db, 'usuarios');
      // const casQuery = query(usuariosRef, where('role', '==', 'CA'));
      
      // const unsubscribe = onSnapshot(casQuery, (snapshot) => {
      //   const lowTicketCAs = [];
      //   const currentAlertedCAs = new Set();
      //   
      //   snapshot.forEach((doc) => {
      //     const data = doc.data();
      //     const caName = data.nombre || data.name || 'CA Desconocido';
      //     const numberCa = data.numberCa || 'N/A';
      //     const ticketsMoto = data.ticketsMoto || 0;
      //     const ticketsAuto = data.ticketsAuto || 0;
      //     
      //     // Verificar si tiene tickets bajos de AUTOS únicamente (menos de 10)
      //     const isLowAuto = ticketsAuto < 10;
      //     
      //     if (isLowAuto) {
      //       const caKey = `${doc.id}-${ticketsMoto}-${ticketsAuto}`;
      //       
      //       lowTicketCAs.push({
      //         id: doc.id,
      //         name: caName,
      //         numberCa: numberCa,
      //         ticketsMoto: ticketsMoto,
      //         ticketsAuto: ticketsAuto,
      //         isLowAuto: isLowAuto,
      //         key: caKey
      //       });
      //       
      //       // Si es una nueva alerta (no estaba antes), tocar sonido
      //       if (!alertedCAs.has(caKey)) {
      //         console.log(`🚨 Dashboard: NUEVA ALERTA - CA ${caName} (${numberCa}) tiene tickets bajos de AUTOS: ${ticketsAuto}`);
      //         currentAlertedCAs.add(caKey);
      //           // Reproducir sonido de alerta
      //         try {
      //           playAlertTone();
      //         } catch (soundError) {
      //           console.warn('Error al reproducir sonido de alerta:', soundError);
      //         }
      //       } else {
      //         currentAlertedCAs.add(caKey);
      //       }
      //     }
      //   });
      //   
      //   console.log(`🚨 Dashboard: Encontrados ${lowTicketCAs.length} CAs con tickets bajos`);
      //   
      //   setLowTicketAlerts(lowTicketCAs);
      //   setShowUrgentAlert(lowTicketCAs.length > 0);
      //   setAlertedCAs(currentAlertedCAs);
      //   
      // }, (error) => {
      //   console.error("🚨 Dashboard: Error en monitoreo de CAs:", error);
      // });

      // Retornar función vacía ya que no hay unsubscribe
      return () => {};
    } catch (error) {
      console.error("🚨 Dashboard: Error al configurar monitoreo de CAs:", error);
      return () => {};
    }
  }, []);

// Crear setupRealtimeTickets con useCallback para evitar la advertencia de exhaustive-deps
  const setupRealtimeTickets = useCallback(() => {
    try {
      // Obtener la fecha actual en formato YYYY-MM-DD
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = (today.getMonth() + 1).toString().padStart(2, '0');
      const dd = today.getDate().toString().padStart(2, '0');
      const fechaActual = `${yyyy}-${mm}-${dd}`;

      // Referencia a la subcolección del día actual
      const ticketsRef = collection(db, 'ticketsVendidos', fechaActual, 'tickets');

      // Escuchar cambios en la subcolección en tiempo real
      const unsubscribe = onSnapshot(ticketsRef, (snapshot) => {
        setRealtimeStatus('Conectado');
        const ticketsList = [];

        snapshot.forEach((doc) => {
          const data = doc.data();
          let startTimeDisplay = data.startTime || 'N/A';
          let endTimeDisplay = data.endTime || 'N/A';
          let endDate = new Date();

          // Procesar startTime - extraer solo la hora
          if (typeof data.startTime === 'string') {
            if (data.startTime.includes(' ')) {
              startTimeDisplay = data.startTime.split(' ')[0];
            } else {
              startTimeDisplay = data.startTime;
            }
          } else if (data.startTime && data.startTime.toDate) {
            const startDate = data.startTime.toDate();
            startTimeDisplay = startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          }

          // Procesar endTime - extraer solo la hora y parsear fecha para determinar estado
          if (typeof data.endTime === 'string') {
            if (data.endTime.includes(' ')) {
              const parts = data.endTime.split(' ');
              endTimeDisplay = parts[0];
              try {
                if (parts.length === 2) {
                  const [time, date] = parts;
                  const [day, month, year] = date.split('/');
                  const [hour, minute] = time.split(':');
                  endDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute));
                }
              } catch (error) {
                console.warn('Error parsing endTime string:', data.endTime, error);
              }
            } else {
              endTimeDisplay = data.endTime;
            }
          } else if (data.endTime && data.endTime.toDate) {
            endDate = data.endTime.toDate();
            endTimeDisplay = endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          }

          ticketsList.push({
            id: doc.id,
            ...data,
            startTime: startTimeDisplay,
            endTime: endTimeDisplay,
            endDate: endDate,
          });
        });

        // Ordenar tickets por orden de registro (más recientes primero)
        ticketsList.sort((a, b) => {
          if (a.createdAt && b.createdAt) {
            let timeA, timeB;
            if (a.createdAt.toDate && b.createdAt.toDate) {
              timeA = a.createdAt.toDate().getTime();
              timeB = b.createdAt.toDate().getTime();
            } else if (typeof a.createdAt === 'string' && typeof b.createdAt === 'string') {
              timeA = new Date(a.createdAt).getTime();
              timeB = new Date(b.createdAt).getTime();
            }
            if (timeA && timeB && !isNaN(timeA) && !isNaN(timeB)) {
              return timeB - timeA;
            }
          }
          const idA = parseInt(a.id);
          const idB = parseInt(b.id);
          if (!isNaN(idA) && !isNaN(idB)) {
            return idB - idA;
          }
          return (b.id || '').localeCompare(a.id || '');
        });

        setTickets([...ticketsList]);
        setLastUpdate(Date.now());
      }, (error) => {
        console.error("🔥 Dashboard REALTIME ERROR:", error);
        setRealtimeStatus('Error');
      });
      return unsubscribe;
    } catch (error) {
      console.error("Error al configurar listener de tickets:", error);
      return () => {};
    }
  }, []);

  // Suscribirse a la configuración de descuento desde Firestore en tiempo real
  useEffect(() => {
    const discountConfigRef = doc(db, 'Fares', 'discountConfig');
    
    const unsubscribeDiscount = onSnapshot(
      discountConfigRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          setDiscountConfig({
            discountEvery: data.discountEvery ?? 0,
            discountPer: data.discountPer ?? 1
          });
        } else {
          setDiscountConfig({
            discountEvery: 0,
            discountPer: 1
          });
        }
      },
      (error) => {
        // En caso de error, usar valores por defecto (sin descuento)
        setDiscountConfig({
          discountEvery: 0,
          discountPer: 1
        });
      }
    );
    
    return () => {
      unsubscribeDiscount();
    };
  }, []);

  // Usar setupRealtimeTickets como dependencia en useEffect
  useEffect(() => {
    if (user) {
      console.log("Configurando escucha de tickets en tiempo real...");
      const unsubscribe = setupRealtimeTickets();
      return () => unsubscribe();
    }
  }, [user, setupRealtimeTickets]);
  // 🚨 useEffect para monitorear CAs con tickets bajos
  useEffect(() => {
    if (user && (userRole === 'Admin' || userRole === 'Supervisor' || userRole === 'Recaudador')) {
      console.log("🚨 Dashboard: Iniciando monitoreo de CAs con tickets bajos...");
      const unsubscribe = setupCATicketsMonitoring();
      return () => unsubscribe();
    }
    // Return empty cleanup function if condition is not met
    return () => {};
  }, [user, userRole, setupCATicketsMonitoring]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          // Verificar si el perfil del usuario existe en Firestore
          const userDocRef = doc(db, 'usuarios', currentUser.email);
          const userDoc = await getDoc(userDocRef);
          
          if (!userDoc.exists()) {
            console.error("Perfil de usuario no encontrado en la base de datos");
            await auth.signOut();
            navigate('/login');
            return;
          }
          
          // El usuario existe, continuar normalmente
          setUser(currentUser);
          
          // Almacenar el rol del usuario
          const userData = userDoc.data();
          setUserRole(userData.rol || 'Usuario');
        } catch (error) {
          console.error("Error al verificar perfil de usuario:", error);
          await auth.signOut();
          navigate('/login');
          return;
        }
      } else {
        navigate('/login');
        return;
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);
  // Calcular estadísticas por tipo de vehículo
  const ticketsByType = tickets.reduce((acc, ticket) => {
    // Normalizar el tipo de vehículo
    let type = ticket.vehicleType;
    if (type === 'Motocicleta') {
      type = 'moto';
    } else if (type === 'Automovil') {
      type = 'auto';
    }
    if (!acc[type]) {
      acc[type] = { total: 0, active: 0, revenue: 0 };
    }
    const dur = Number(ticket.duration) || 1;
    acc[type].total += dur;
    acc[type].revenue += (ticket.price || 0) * dur;
    // Verificar si está activo
    const now = new Date();
    if (ticket.endDate > now) {
      acc[type].active += dur;
    }
    return acc;
  }, {});

  // Estadísticas específicas por tipo SIN descuento
  const rawMotoStats = ticketsByType.moto || { total: 0, active: 0, revenue: 0 };
  const rawAutoStats = ticketsByType.auto || { total: 0, active: 0, revenue: 0 };
  
  // Aplicar descuento por separado a cada tipo de vehículo
  const motoTotalConDescuento = applyTicketDiscount(rawMotoStats.total);
  const autoTotalConDescuento = applyTicketDiscount(rawAutoStats.total);
  
  // El total debe ser la SUMA de los descuentos aplicados por tipo
  const totalTickets = motoTotalConDescuento + autoTotalConDescuento;
  
  // Calcular tickets activos
  const activeTickets = tickets.filter(ticket => {
    const now = new Date();
    return ticket.endDate > now;
  }).reduce((acc, ticket) => acc + (Number(ticket.duration) || 1), 0);
  
  
  const motoStats = {
    ...rawMotoStats,
    total: motoTotalConDescuento
  };
  const autoStats = {
    ...rawAutoStats,
    total: autoTotalConDescuento
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100 transition-all duration-300 ease-in-out">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  return (
    <div className="flex flex-col h-screen bg-gray-100 transition-all duration-300 ease-in-out">      {/* Mobile Header */}
      <MobileHeader 
        title="Dashboard - Tickets de Hoy" 
        userEmail={user?.email}
        userRole={userRole}
        toggleMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />
      
      {/* 🚨 Alerta Urgente para CAs con tickets bajos */}
      {/* {showUrgentAlert && lowTicketAlerts.length > 0 && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-red-600 text-white p-4 shadow-lg animate-pulse">
          <div className="container mx-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="animate-bounce">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-lg">🚨 RECARGA URGENTE NECESARIA - AUTOS</h3>
                  <p className="text-sm">
                    {lowTicketAlerts.length} CA{lowTicketAlerts.length > 1 ? 's' : ''} con tickets de autos críticos (menos de 10)
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowUrgentAlert(false)}
                className="text-white hover:text-gray-200 text-xl font-bold"
              >
                ✕
              </button>
            </div>
            <div className="mt-3 max-h-32 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {lowTicketAlerts.map((ca) => (
                  <div key={ca.key} className="bg-red-700 p-2 rounded text-sm">
                    <div className="font-semibold">{ca.name} ({ca.numberCa})</div>
                    <div className="flex space-x-2 text-xs">
                      <span className="text-yellow-300">🚗 Autos: {ca.ticketsAuto}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )} */}
      
      {/* Add top spacing for mobile to prevent content from being hidden under header */}
      <div className={`md:hidden ${/* showUrgentAlert ? 'h-32' : */'h-14'}`}></div>
      
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <Sidebar 
          activeSection={activeSection} 
          setActiveSection={setActiveSection} 
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />

        {/* Main Content */}
        <div className="flex-1 overflow-auto transition-all duration-300 ease-in-out">          {/* Top Header (desktop only) */}
          <header className="bg-white shadow px-6 py-4 hidden md:block">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-xl font-bold text-gray-900">Dashboard</h1>                <p className="text-sm text-gray-600">
                  📅 Tickets de hoy - {new Date().toLocaleDateString('es-ES', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                  <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                    realtimeStatus === 'Conectado' ? 'bg-green-100 text-green-800' : 
                    realtimeStatus === 'Error' ? 'bg-red-100 text-red-800' : 
                    'bg-gray-100 text-gray-800'
                  }`}>
                    🔄 {realtimeStatus}
                  </span>
                </p>
              </div>
              <div className="text-right flex items-center">
                <div className="mr-4">
                  <p className="text-sm font-medium text-gray-900">{user?.email}</p>
                  <p className="text-xs text-gray-500">{userRole}</p>
                </div>
                <button 
                  onClick={async () => {
                    try {
                      await auth.signOut();
                      navigate('/login');
                    } catch (error) {
                      console.error("Error al cerrar sesión:", error);
                    }
                  }}
                  className="bg-red-500 hover:bg-red-600 text-white text-xs px-3 py-1 rounded"
                >
                  Cerrar Sesión
                </button>
              </div>
            </div>
          </header>          {/* Content */}
          <main className="p-6">            {/* Date Info for Mobile */}
            <div className="md:hidden mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800 font-medium">
                📅 {new Date().toLocaleDateString('es-ES', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
                <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                  realtimeStatus === 'Conectado' ? 'bg-green-100 text-green-800' : 
                  realtimeStatus === 'Error' ? 'bg-red-100 text-red-800' : 
                  'bg-gray-100 text-gray-800'
                }`}>
                  🔄 {realtimeStatus}
                </span>
              </p>
            </div>
            
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              {/* Total General */}
              <StatCard 
                title="Total Tickets Hoy" 
                value={totalTickets} 
                bgColor="bg-white"
                icon={
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                  </svg>
                }
                textColor="text-gray-700"
              />

              {/* Tickets Motos */}
              <StatCard 
                title="🏍️ Tickets Motos" 
                value={motoStats.total} 
                bgColor="bg-white"
                icon={
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2M7 4h10M7 4l-2 14h14l-2-14" />
                  </svg>
                }
                textColor="text-blue-600"
              />

              {/* Tickets Autos */}
              <StatCard 
                title="🚗 Tickets Autos" 
                value={autoStats.total} 
                bgColor="bg-white"
                icon={
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2H5a2 2 0 01-2-2v2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                }
                textColor="text-green-600"
              />
              
              {/* Tickets Activos Total */}
              <StatCard 
                title="⏰ Tickets Activos" 
                value={activeTickets} 
                bgColor="bg-white"
                icon={
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                }
                textColor="text-yellow-600"
              />
            </div>            {/* Tarjetas de resumen por tipo de vehículo */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Resumen Motos */}
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg shadow p-6 border-l-4 border-blue-500">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-blue-800 mb-2">🏍️ Resumen Motos</h3>
                    <div className="space-y-1">
                      <p className="text-sm text-blue-700">Total tickets: <span className="font-semibold">{motoStats.total}</span></p>
                      <p className="text-sm text-blue-700">Activos: <span className="font-semibold">{motoStats.active}</span></p>
                    </div>
                  </div>
                  <div className="text-4xl text-blue-500">🏍️</div>
                </div>
              </div>

              {/* Resumen Autos */}
              <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg shadow p-6 border-l-4 border-green-500">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-green-800 mb-2">🚗 Resumen Autos</h3>
                    <div className="space-y-1">
                      <p className="text-sm text-green-700">Total tickets: <span className="font-semibold">{autoStats.total}</span></p>
                      <p className="text-sm text-green-700">Activos: <span className="font-semibold">{autoStats.active}</span></p>
                    </div>
                  </div>
                  <div className="text-4xl text-green-500">🚗</div>
                </div>
              </div>
            </div>
              {/* Recent Tickets Table */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-gray-800">Tickets Activos</h2>
                  <p className="text-xs text-gray-500">
                    Última actualización: {new Date(lastUpdate).toLocaleTimeString()}
                  </p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ID
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Placa
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tipo
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Duración
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Zona
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        CA
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Inicio
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fin
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tickets
                      .filter(ticket => {
                        const now = new Date();
                        return ticket.endDate > now;
                      })
                      .map((ticket) => {
                        // Determine background color based on vehicle type
                        const getVehicleTypeClass = (vehicleType) => {
                          if (vehicleType === 'auto' || vehicleType === 'Automovil') {
                            return 'bg-blue-50'; // Light blue for cars
                          } else if (vehicleType === 'moto' || vehicleType === 'Motocicleta') {
                            return 'bg-green-50'; // Light green for motorcycles
                          } else if (vehicleType === 'exonerado') {
                            return 'bg-yellow-50'; // Light yellow for exempt
                          }
                          return 'bg-white'; // Default white
                        };

                        return (
                          <tr key={ticket.id} className={getVehicleTypeClass(ticket.vehicleType)}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{ticket.id}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{ticket.plate}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {ticket.vehicleType === 'auto' ? 'Auto' : 
                                 ticket.vehicleType === 'moto' ? 'Moto' : 
                                 ticket.vehicleType === 'Motocicleta' ? 'Moto' :
                                 ticket.vehicleType === 'Automovil' ? 'Auto' :
                                 ticket.vehicleType === 'exonerado' ? 'Exonerado' : 
                                 ticket.vehicleType || 'Desconocido'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {ticket.duration} {ticket.duration === 1 ? 'hora' : 'horas'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {ticket.zone || ticket.zona}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div className="font-medium">
                                {ticket.numberCa || 'N/A'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {ticket.startTime}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {ticket.endTime}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                Activo
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    {tickets.filter(ticket => ticket.endDate > new Date()).length === 0 && (
                      <tr>
                        <td colSpan="9" className="px-6 py-4 text-center text-gray-500">
                          No hay tickets activos para mostrar.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
